/* eslint-disable no-shadow */
import React from 'react';
import PropTypes from 'prop-types';
import {action, computed, observable, reaction} from 'mobx';
import {inject, observer} from 'mobx-react';
import {withRouter, locationShape} from 'react-router';
import autobind from 'autobind-decorator';

import {browserOnly} from 'spa/Components/EnvSplit';
import {cn} from 'spa/css';
import {withI18n} from './Context';

import SyncSpinner, {IconSizes} from '@urbanmassage/component-library/lib/Icons/SyncSpinner/SyncSpinner';
import Typography, { TypeVariants } from '@urbanmassage/component-library/lib/Typography/Typography';

import Select from 'spa/Components/Form/Select';
import TypeaheadField from './TypeaheadField';

import './address-field-2021.less';

const ADDRESS_PARTS_SEP = ', ';

@browserOnly
@withRouter
@inject(({
  store: {
    session,
    tracker,
    sdk,
    userAddresses,
  },
}) => ({
  session,
  tracker,
  sdk,
  userAddresses,
}))
@observer
@withI18n
class AddressField2021 extends React.Component {
  static propTypes = {
    addressConfirmed: PropTypes.bool,
    align: PropTypes.oneOf(['left', 'right']),
    canLoadCustom: PropTypes.bool,
    className: PropTypes.string,
    clearStyles: PropTypes.object,
    darkBackground: PropTypes.bool,
    defaultValue: PropTypes.string,
    disableSavedAddresses: PropTypes.bool,
    hideClearOnEmpty: PropTypes.bool,
    includeClear: PropTypes.bool,
    listClassName: PropTypes.string,
    location: locationShape.isRequired,
    onClear: PropTypes.func,
    onSelect: PropTypes.func,
    router: PropTypes.object,
    params: PropTypes.object,
    placeholder: PropTypes.string,
    roundBorder: PropTypes.bool,
    routes: PropTypes.array,
    sdk: PropTypes.object.isRequired,
    selectedAddressId: PropTypes.string,
    selectZIndex: PropTypes.number,
    session: PropTypes.object.isRequired,
    skipInitialLoad: PropTypes.bool,
    tracker: PropTypes.object.isRequired,
    trackingCategory: PropTypes.string,
    userAddresses: PropTypes.object.isRequired,
  };

  static defaultProps = {
    align: 'right',
    hideClearOnEmpty: false,
    listClassName: undefined,
  };

  constructor(props) {
    super(props);

    const {
      defaultValue, disableSavedAddresses, userAddresses, session,
    } = props;

    if (
      session.user &&
      !userAddresses.isLoading &&
      !userAddresses.isLoaded &&
      !defaultValue &&
      !disableSavedAddresses
    ) {
      userAddresses.maybeLoad();
    }
  }

  async componentDidMount() {
    const {userAddresses} = this.props;

    if (
      !this.props.skipInitialLoad &&
      !this.props.defaultValue
    ) {
      const methodsToTry = [this.loadLastChoice];

      for (const method of methodsToTry) {
        try {
          if (await method()) { break; }
        } catch (error) {
          // do nothing
        }
      }
    }

    this.listeners = [
      reaction(
        () => this.typeaheadField,
        (typeaheadField) => {
          if (typeaheadField && this.props.canLoadCustom && this.props.defaultValue) {
            this.typeaheadField.setValue(this.props.defaultValue);
          }
        },
        {
          name: 'Update custom address field with value of selected address',
          fireImmediately: true,
        },
      ),
      reaction(
        () => userAddresses && userAddresses.allAddresses,
        (addressList) => {
          if (addressList.length) {
            this.userAddressesList = addressList.map((address) => {
              this.addressTypesList[address._id] = {
                type: address.type,
                value: `latlng:${address.lat},${address.lng}`,
              };

              return address;
            });

            if (
              !this.selectedAddress &&
              !this.props.disableSavedAddresses &&
              this.useSavedAddresses
            ) {
              this.selectedAddress = addressList[0]._id;

              this.props.onSelect({address_id: this.selectedAddress});
            }
          }
        },
        {
          name: 'Select first saved address by default',
          compareStructural: true,
          fireImmediately: true,
        },
      ),
      reaction(
        () => this.props.session && this.props.session.user,
        (user) => {
          if (user) {
            const {userAddresses} = this.props;
            userAddresses.maybeLoad();
          } else {
            this.selectedAddress = undefined;
          }
        },
        {
          name: 'Load user addresses when user signs in',
          compareStructural: true,
          fireImmediately: true,
        },
      ),
    ];
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.defaultValue !== nextProps.defaultValue &&
      nextProps.defaultValue != null &&
      this.typeaheadField
    ) {
      this.typeaheadField.setValue(nextProps.defaultValue);
    }
  }

  componentWillUnmount() {
    while (this.listeners && this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable addressTypesList = [];
  @observable selectedAddress = undefined;
  @observable typeaheadField = undefined;
  @observable userAddressesList;
  @observable useSavedAddresses = true;

  @autobind
  async loadLastChoice() {
    const {onSelect} = this.props;

    const lastTypeaheadSelection = await this.props.session.getCachedMeta('lastTypeaheadSelection');
    if (lastTypeaheadSelection && this.typeaheadField) {
      const {resultSource, result_id, term} = lastTypeaheadSelection;

      if (term) {
        this.typeaheadField.setValue(term);
      }

      if (onSelect) {
        onSelect({
          place_id: result_id,
          provider_id: resultSource,
        });
      }

      return true;
    }

    const lastSearchPostcode = await this.props.session.getCachedMeta('lastSearchPostcode');
    if (lastSearchPostcode && this.typeaheadField) {
      this.typeaheadField.setValue(lastSearchPostcode);

      if (onSelect) {
        onSelect({
          place_id: lastSearchPostcode,
          provider_id: 'ukpostcode',
        });
      }
      return true;
    }
    return false;
  }
  /*
   *  onClick - when user clicks the custom address search field
   */
  @autobind onClick(event) {
    if (event) event.preventDefault();
    this.props.tracker.track('Clicked Location Address Field', {
      category: this.props.trackingCategory,
    });
  }

  /*
   *  changeAddress - when user changes the address
   */
  @autobind
  @action('changeAddress - AddressField')
  changeAddress(event) {
    const {onSelect} = this.props;

    this.selectedAddress = event.target.value;

    if (this.selectedAddress === 'newAddress') {
      this.useSavedAddresses = false;

      onSelect(null);

      setTimeout(() => {
        if (this.typeaheadField) {
          this.typeaheadField.setValue('');
        }
      }, 100);
    } else {
      onSelect({
        address_id: this.selectedAddress,
      });
    }
  }

  /*
   *  onClickClear - user clears custom address field
   */
  @autobind
  @action('onClickClear - AddressField')
  onClickClear(event) {
    setTimeout(() => {
      if (this.typeaheadField) {
        this.typeaheadField.setValue('');
      }
    }, 100);

    const {onClear, onSelect} = this.props;
    const {addressesSelectList, userAddressesList} = this;

    onClear();

    if (userAddressesList && userAddressesList.length) {
      this.useSavedAddresses = true;

      this.selectedAddress = addressesSelectList[0].value;

      onSelect({
        address_id: this.selectedAddress,
      });
    }
  }

  /*
   *  addressesSelectList - object with list of user saved addresses for the select element
   */
  @computed get addressesSelectList() {
    const {i18n} = this.context;
    const {userAddressesList} = this;
    if (!userAddressesList) return null;

    const addressOptions = userAddressesList.map((address) => {
      const {type} = address;
      let label = '';

      switch (type) {
      case 'custom':
        label = i18n._x('Custom', 'Address field - saved addresses selector');
        break;
      case 'home':
        label = i18n._x('Home', 'Address field - saved addresses selector');
        break;
      case 'work':
        label = i18n._x('Work', 'Address field - saved addresses selector');
        break;
      default:
        label = i18n._x('Custom', 'Address field - saved addresses selector');
      }

      return {
        key: address._id,
        label,
        subtext: `${address.address1}, ${address.postcode}`,
        value: address._id,
        className: `address-field-2021__option address-field-2021__option--${address.type}`,
      };
    });

    addressOptions.push({
      label: i18n._x('New Address', 'Address search field component'),
      value: 'newAddress',
      className: `address-field-2021__option address-field-2021__option--new`,
    });

    return addressOptions;
  }

  /*
   *  getSuggestionsForString - get address sugestions as user types
   */
  getSuggestionsForString = term =>
    this.props.sdk.get(`/search/location/typeahead?term=${encodeURIComponent(term)}`)
      .then(({json}) =>
        json.results.map(({
          description, result_id, resultSource, matched_substrings,
        }) => {
          // split on last comma
          const parts = description.split(ADDRESS_PARTS_SEP);
          let subtext = '';
          if (parts.length > 2) {
            subtext = ADDRESS_PARTS_SEP +
              parts
                .splice(parts.length - 2, 2)
                .join(ADDRESS_PARTS_SEP);
          } else if (parts.length > 1) {
            subtext = ADDRESS_PARTS_SEP + parts.pop();
          }
          const name = parts.join(ADDRESS_PARTS_SEP);

          return {
            term: description,
            name,
            subtext,
            matched_substrings,
            value: `${resultSource}:${result_id}`,
            resultSource,
            place_id: result_id,
            provider_id: resultSource,
          };
        }));

  @autobind inputRef(input) {
    this.typeaheadField = input;
  }

  render() {
    const {userAddressesList, useSavedAddresses, onClickClear} = this;
    const {i18n} = this.context;
    /* eslint-disable no-unused-vars */
    const {
      addressConfirmed,
      align,
      canLoadCustom,
      className,
      clearStyles,
      darkBackground,
      defaultValue,
      disableSavedAddresses,
      hideClearOnEmpty,
      includeClear,
      listClassName,
      location,
      onClear,
      params,
      placeholder,
      roundBorder,
      router,
      routes,
      sdk,
      selectedAddressId,
      selectZIndex,
      session,
      skipInitialLoad,
      tracker,
      trackingCategory,
      userAddresses,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */

    if (session && session.user && userAddresses.isLoading) {
      return (
        <div className="address-field-2021__container address-field-2021__loading-container" aria-live="polite" aria-busy="true">
          <SyncSpinner className="address-field-2021__loading-icon" size={IconSizes.default} />
          <Typography className="address-field-2021__loading-text">
            {i18n._x('Loading addresses....', 'Address picker')}
          </Typography>
        </div>
      );
    }

    if (!userAddressesList || !useSavedAddresses || disableSavedAddresses) {
      return (
        <div className="address-field-2021__container" aria-live="polite">
          <TypeaheadField
            hideClearOnEmpty={hideClearOnEmpty}
            placeholder={placeholder || i18n._x('enter your address', 'Address field')}
            type="text"
            {...rest}
            ref={this.inputRef}
            className={`form-control address-field-2021 ${className || ''}`}
            getSuggestionsForString={this.getSuggestionsForString}
            name="address-field-2021-typeahead"
            selectFirstOptionOnEnterKey
            onClear={onClickClear}
            includeClear
            clearStyles={clearStyles}
            onClick={this.onClick}
            zIndex={selectZIndex}
          />
        </div>
      );
    }

    return (
      <div className="address-field-2021__container" aria-live="polite">
        <Select
          align={align}
          className={cn(
            'address-field-2021__select',
            darkBackground && 'address-field-2021__select--dark',
            roundBorder && 'address-field-2021__select--roundBorder',
            this.addressTypesList[this.selectedAddress] && `address-field-2021__select--${this.addressTypesList[this.selectedAddress].type}`,
          )}
          classNameDisabled=""
          classNameOpen=""
          listClassName={listClassName}
          name="address-field-2021"
          onChange={this.changeAddress}
          options={this.addressesSelectList}
          value={this.selectedAddress}
          zIndex={selectZIndex}
        />
      </div>
    );
  }
}

export default AddressField2021;



// WEBPACK FOOTER //
// ./spa/Components/AddressField2021.jsx